import React, { useState } from "react";
import theme from "../../styles/theme/theme";
import { Container } from "@mui/material";
import { Shield, Mail, Headphones, Zap, Megaphone } from "lucide-react";
import Seo from "../../components/Seo";

import Breadcrumb from "../../components/Breadcrumb";
import { generateBreadcrumbs } from "../../utils/breadcrumbs";
import { useDispatch, useSelector } from "react-redux";
import { contactUs as contactUsAction } from "../../features/buyer/buyerSlice";

const contactDetails = [
  {
    icon: <Shield color={theme.palette.primary.main} size={24} />,
    title: "General Administration",
    email: "admin@scraptrade.com.au",
    description:
      "For official correspondence, documentation, account matters, or anything that doesn't neatly fit elsewhere. If it's admin-related, this is your go-to inbox.",
  },
  {
    icon: <Mail color={theme.palette.primary.main} size={24} />,
    title: "General Enquiries",
    email: "info@scraptrade.com.au",
    description:
      "Not sure who to contact? Start here. This inbox handles general questions about our services, platform usage, onboarding, and basic how-it-works queries.",
  },
  {
    icon: <Headphones color={theme.palette.primary.main} size={24} />,
    title: "Customer Support",
    email: "cs@scraptrade.com.au",
    description:
      "Having an issue? Need help with your account, transactions, or platform activity? Our customer support team monitors this inbox closely to resolve problems quickly and clearly — no copy-paste answers.",
  },
  {
    icon: <Zap color={theme.palette.primary.main} size={24} />,
    title: "Technical Support",
    email: "techsupport@scraptrade.com.au",
    description:
      "Experiencing technical glitches, login issues, bugs, or performance problems? Contact our tech team directly so issues can be diagnosed and fixed without delays.",
  },
  {
    icon: <Megaphone color={theme.palette.primary.main} size={24} />,
    title: "Marketing & Partnerships",
    email: "marketing@scraptrade.com.au",
    description:
      "For media enquiries, partnerships, advertising opportunities, or collaboration ideas. If it involves promotion, branding, or strategic growth — this is the right channel.",
  },
];

const breadcrumbs = generateBreadcrumbs({ pageType: "contactus" });

const ContactUs = () => {
  const { contactUsLoading, contactUsError } = useSelector(
    (state) => state.buyer,
  );
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [hoveredEmail, setHoveredEmail] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\+?[0-9\s\-()]{7,15}$/.test(formData.phone)) {
      newErrors.phone = "Enter a valid phone number";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    dispatch(contactUsAction(formData))
      .unwrap()
      .then(() => {
        // reset form on success
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
        alert("Message sent successfully!");
      })
      .catch(() => {
        // error is already in redux state
      });
  };

  return (
    <>
      <Seo
        title="Contact ScrapTrade | Connect With Our Team"
        description="Have questions about scrap trading? Contact ScrapTrade for platform support, pricing help, partnerships, and customer service you can trust."
        url="https://scraptrade.com.au/contact"
        canonical="https://scraptrade.com.au/contact"
        image="https://scraptrade.com.au/assets/og/scrap.jpg"
        breadcrumbs={breadcrumbs}
        extraSchema={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+61-4415-9165",
            contactType: "Customer Service",
            areaServed: "Worldwide",
          },
        }}
      />

      <div
        style={{
          padding: theme.spacing(8),
          background: theme.palette.background.paper,
          minHeight: "100vh",
        }}
      >
        <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
          <Breadcrumb items={breadcrumbs} />
        </Container>

        {/* Page Title */}
        <div style={{ textAlign: "center", marginBottom: theme.spacing(12) }}>
          <h2
            style={{
              ...theme.typography.h3,
              color: theme.palette.primary.main,
              position: "relative",
              display: "inline-block",
              paddingBottom: theme.spacing(2),
              marginBottom: 0,
            }}
          >
            Contact Us
            <span
              style={{
                content: '""',
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                bottom: 0,
                width: "60%",
                height: 4,
                background: theme.palette.primary.main,
                borderRadius: 2,
                display: "block",
              }}
            ></span>
          </h2>
          <div
            style={{
              ...theme.typography.h6,
              color: theme.palette.text.secondary,
              marginTop: theme.spacing(4),
              maxWidth: 600,
              marginLeft: "auto",
              marginRight: "auto",
              lineHeight: 1.7,
            }}
          >
            Got a question or need help? We're here for you.
          </div>
        </div>

        {/* Responsive Flex Container */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: theme.spacing(8),
            alignItems: "stretch",
            flexWrap: "wrap",
          }}
        >
          {/* Contact Information */}
          <div
            style={{
              flex: 1,
              background: theme.palette.background.paper,
              borderRadius: theme.shape.borderRadius,
              boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
              padding: theme.spacing(8),
              minWidth: 320,
              display: "flex",
              flexDirection: "column",
              marginBottom: theme.spacing(8),
            }}
          >
            <div
              style={{
                ...theme.typography.h5,
                fontWeight: 600,
                marginBottom: theme.spacing(4),
                color: theme.palette.text.primary,
              }}
            >
              Contact Details
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(4),
                marginTop: theme.spacing(4),
              }}
            >
              {contactDetails.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: theme.spacing(3),
                    background: theme.palette.background.default,
                    padding: theme.spacing(4),
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  }}
                >
                  <span
                    style={{
                      fontSize: "1.5rem",
                      marginTop: "4px",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        ...theme.typography.body1,
                        fontWeight: 600,
                        marginBottom: theme.spacing(1),
                        color: theme.palette.text.primary,
                      }}
                    >
                      {item.title}
                    </div>
                    <a
                      href="#"
                      onMouseEnter={() => setHoveredEmail(item.email)}
                      onMouseLeave={() => setHoveredEmail(null)}
                      onClick={(e) => {
                        e.preventDefault();
                        // Copy email to clipboard
                        navigator.clipboard.writeText(item.email).then(() => {
                          alert(`Email copied: ${item.email}`);
                        }).catch(() => {
                          alert(`Email: ${item.email}`);
                        });
                      }}
                      title="Click to copy email"
                      style={{
                        ...theme.typography.body2,
                        color: theme.palette.tertiary.main,
                        marginBottom: theme.spacing(1.5),
                        fontWeight: 500,
                        textDecoration:
                          hoveredEmail === item.email ? "underline" : "none",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      {item.email}
                    </a>
                    <div
                      style={{
                        ...theme.typography.body2,
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                      }}
                    >
                      {item.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* <div style={{ borderTop: `1px solid ${theme.palette.secondary.main}`, margin: `${theme.spacing(8)} 0` }} />
          <div
            style={{
              borderRadius: theme.shape.borderRadius,
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
              height: 220,
              marginTop: "auto",
            }}
          >
            <iframe
              title="map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.4043548502953!2d144.955651!3d-37.817327!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad65d43e2f2f3b7%3A0x1c8f4bb2e3a183fa!2sSome%20Fake%20Location!5e0!3m2!1sen!2sin!4v1585288090862!5m2!1sen!2sin"
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>*/}
          </div>

          {/* Contact Form */}
          <div
            style={{
              flex: 1,
              background: theme.palette.background.paper,
              borderRadius: theme.shape.borderRadius,
              boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
              padding: theme.spacing(8),
              minWidth: 320,
              display: "flex",
              flexDirection: "column",
              marginBottom: theme.spacing(8),
            }}
          >
            <div
              style={{
                ...theme.typography.h5,
                fontWeight: 600,
                marginBottom: theme.spacing(4),
                color: theme.palette.text.primary,
              }}
            >
              Send Us a Message
            </div>
            <form
              onSubmit={handleSubmit}
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between", // pushes the button to bottom
                flex: 1,
                gap: theme.spacing(6),
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: theme.spacing(6),
                }}
              >
                <input
                  type="text"
                  placeholder="Your Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  style={{
                    ...theme.typography.body1,
                    padding: theme.spacing(6),
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.primary.main}`,
                    outline: "none",
                  }}
                />
                {errors.name && (
                  <span style={{ color: "red", fontSize: "0.85rem" }}>
                    {errors.name}
                  </span>
                )}
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  style={{
                    ...theme.typography.body1,
                    padding: theme.spacing(6),
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.primary.main}`,
                    outline: "none",
                  }}
                />
                {errors.email && (
                  <span style={{ color: "red", fontSize: "0.85rem" }}>
                    {errors.email}
                  </span>
                )}

                <input
                  type="text"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  style={{
                    ...theme.typography.body1,
                    padding: theme.spacing(6),
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.primary.main}`,
                    outline: "none",
                  }}
                />
                {errors.phone && (
                  <span style={{ color: "red", fontSize: "0.85rem" }}>
                    {errors.phone}
                  </span>
                )}
                <input
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleChange}
                  style={{
                    ...theme.typography.body1,
                    padding: theme.spacing(6),
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.primary.main}`,
                    outline: "none",
                  }}
                />
                {errors.subject && (
                  <span style={{ color: "red", fontSize: "0.85rem" }}>
                    {errors.subject}
                  </span>
                )}
                <textarea
                  name="message"
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  style={{
                    ...theme.typography.body1,
                    padding: theme.spacing(6),
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.primary.main}`,
                    outline: "none",
                    resize: "vertical",
                  }}
                />
                {errors.message && (
                  <span style={{ color: "red", fontSize: "0.85rem" }}>
                    {errors.message}
                  </span>
                )}
              </div>
              <button
                type="submit"
                disabled={contactUsLoading}
                style={{
                  ...theme.typography.button,
                  background: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  border: "none",
                  borderRadius: theme.shape.borderRadius,
                  padding: `${theme.spacing(6)} 0`,
                  fontWeight: 600,
                  fontSize: "1.1rem",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  transition: `background ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
                  marginTop: theme.spacing(4),
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background =
                    theme.palette.tertiary.main)
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.background =
                    theme.palette.primary.main)
                }
              >
                {contactUsLoading ? "Submitting..." : "Submit"}
              </button>
              {contactUsError && (
                <p style={{ color: "red", marginTop: theme.spacing(2) }}>
                  {contactUsError}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactUs;
